import React from 'react';
import { useApp } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import AlertCard from '../../components/shared/AlertCard';
import { Bell, AlertTriangle, Check, Clock } from 'lucide-react';

const AlertsPage = () => {
  const { currentUser, getSubscriberAlerts } = useApp();
  const alerts = getSubscriberAlerts(currentUser?.id);

  const newAlerts = alerts.filter(a => a.status === 'New');
  const acknowledgedAlerts = alerts.filter(a => a.status === 'Acknowledged');
  const resolvedAlerts = alerts.filter(a => a.status === 'Resolved');
  const criticalAlerts = alerts.filter(a => a.severity === 'Critical');

  return (
    <div className="min-h-screen bg-cream" data-testid="alerts-page">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="font-serif text-2xl font-bold text-gray-900">
            Alerts
          </h1>
          <p className="text-gray-500 mt-1">
            Stay informed about important updates for your dependants
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white rounded-2xl border border-gray-100 hover:shadow-md transition-shadow">
            <CardContent className="p-5 text-center">
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mx-auto mb-3">
                <Bell className={`w-6 h-6 text-red-600 ${newAlerts.length > 0 ? 'animate-pulse' : ''}`} />
              </div>
              <p className="text-3xl font-serif font-bold text-red-600">{newAlerts.length}</p>
              <p className="text-sm text-gray-500 mt-1">New</p>
            </CardContent>
          </Card>

          <Card className="bg-white rounded-2xl border border-gray-100 hover:shadow-md transition-shadow">
            <CardContent className="p-5 text-center">
              <div className="w-12 h-12 rounded-xl bg-yellow-50 flex items-center justify-center mx-auto mb-3">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <p className="text-3xl font-serif font-bold text-yellow-600">{acknowledgedAlerts.length}</p>
              <p className="text-sm text-gray-500 mt-1">Acknowledged</p>
            </CardContent>
          </Card>

          <Card className="bg-white rounded-2xl border border-gray-100 hover:shadow-md transition-shadow">
            <CardContent className="p-5 text-center">
              <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center mx-auto mb-3">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-3xl font-serif font-bold text-green-600">{resolvedAlerts.length}</p>
              <p className="text-sm text-gray-500 mt-1">Resolved</p>
            </CardContent>
          </Card>

          <Card className="bg-white rounded-2xl border border-gray-100 hover:shadow-md transition-shadow">
            <CardContent className="p-5 text-center">
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <p className="text-3xl font-serif font-bold text-red-600">{criticalAlerts.length}</p>
              <p className="text-sm text-gray-500 mt-1">Critical</p>
            </CardContent>
          </Card>
        </div>

        {/* Alerts List */}
        <Card className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <CardContent className="p-0">
            <Tabs defaultValue="all" className="w-full">
              <div className="border-b border-gray-100 px-5">
                <TabsList className="bg-transparent h-14">
                  <TabsTrigger value="all" className="rounded-full">All ({alerts.length})</TabsTrigger>
                  <TabsTrigger value="new" className="rounded-full">New ({newAlerts.length})</TabsTrigger>
                  <TabsTrigger value="critical" className="rounded-full">Critical ({criticalAlerts.length})</TabsTrigger>
                  <TabsTrigger value="resolved" className="rounded-full">Resolved ({resolvedAlerts.length})</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="all" className="p-5 space-y-3">
                {alerts.map(alert => (
                  <AlertCard key={alert.id} alert={alert} />
                ))}
                {alerts.length === 0 && (
                  <div className="text-center py-12">
                    <Check className="w-12 h-12 text-green-400 mx-auto mb-3" />
                    <p className="text-gray-500">No alerts</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="new" className="p-5 space-y-3">
                {newAlerts.map(alert => (
                  <AlertCard key={alert.id} alert={alert} />
                ))}
                {newAlerts.length === 0 && (
                  <div className="text-center py-12">
                    <Check className="w-12 h-12 text-green-400 mx-auto mb-3" />
                    <p className="text-gray-500">No new alerts</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="critical" className="p-5 space-y-3">
                {criticalAlerts.map(alert => (
                  <AlertCard key={alert.id} alert={alert} />
                ))}
                {criticalAlerts.length === 0 && (
                  <div className="text-center py-12">
                    <Check className="w-12 h-12 text-green-400 mx-auto mb-3" />
                    <p className="text-gray-500">No critical alerts</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="resolved" className="p-5 space-y-3">
                {resolvedAlerts.map(alert => (
                  <AlertCard key={alert.id} alert={alert} />
                ))}
                {resolvedAlerts.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No resolved alerts yet</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AlertsPage;
